import { useState, useEffect } from "react";
import { useApi } from "@/lib/api";
import { 
  GitCompare, 
  Plus, 
  X, 
  ArrowRight,
  CheckCircle2,
  XCircle,
  Globe,
  Building2,
  Calendar,
  FileText,
  ExternalLink,
  Pencil,
  History
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ViewHistoryButton } from "@/components/ChangelogViewer";

export default function CountryComparison() {
  const api = useApi();
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countryDetails, setCountryDetails] = useState({});

  useEffect(() => {
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedCountries.length >= 2) {
      fetchComparison();
    } else {
      setComparisonData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountries]);

  const fetchCountries = async () => {
    try {
      const response = await api.get("/api/countries/");
      setCountries(response.data?.countries || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchComparison = async () => {
    if (selectedCountries.length < 2) return;
    
    setLoading(true);
    try {
      const codes = selectedCountries.join(",");
      const response = await api.get(`/api/countries/compare?codes=${codes}`);
      setComparisonData(response.data);
      
      // Fetch full details for each country
      const details = {};
      for (const code of selectedCountries) {
        const detailRes = await api.get(`/api/countries/${code}`);
        details[code] = detailRes.data;
      }
      setCountryDetails(details);
    } catch (error) {
      console.error("Error fetching comparison:", error);
    } finally {
      setLoading(false);
    }
  };

  const addCountry = (code) => {
    if (selectedCountries.length < 4 && !selectedCountries.includes(code)) {
      setSelectedCountries([...selectedCountries, code]);
    }
  };

  const removeCountry = (code) => {
    setSelectedCountries(selectedCountries.filter(c => c !== code));
  };

  const availableCountries = countries.filter(c => !selectedCountries.includes(c.country_code));

  return (
    <div className="content-wrapper" data-testid="country-comparison-page">
      <div className="page-header compact">
        <h1 className="page-title">Country Comparison</h1>
        <p className="page-subtitle">
          Compare EPR/PPR requirements side-by-side
        </p>
      </div>

      {/* Country Selector - Compact */}
      <div className="card p-4 mb-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-1.5">
            <GitCompare className="w-4 h-4 text-violet-600" />
            <span className="font-medium text-zinc-700 text-sm">Compare:</span>
          </div>
          
          <div className="flex flex-wrap items-center gap-1.5 flex-1">
            {selectedCountries.map(code => {
              const country = countries.find(c => c.country_code === code);
              return (
                <Badge 
                  key={code} 
                  variant="secondary" 
                  className="px-2 py-1 text-xs flex items-center gap-1.5"
                  data-testid={`selected-country-${code}`}
                >
                  <span>{country?.flag_emoji}</span>
                  <span>{country?.country_name || code}</span>
                  <button 
                    onClick={() => removeCountry(code)}
                    className="ml-0.5 hover:text-red-500"
                    data-testid={`remove-country-${code}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              );
            })}
            
            {selectedCountries.length < 4 && (
              <Select onValueChange={addCountry}>
                <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="add-country-select">
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  <SelectValue placeholder="Add country" />
                </SelectTrigger>
                <SelectContent>
                  {availableCountries.map(country => (
                    <SelectItem key={country.country_code} value={country.country_code}>
                      <span className="flex items-center gap-1.5">
                        <span>{country.flag_emoji}</span>
                        <span>{country.country_name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {selectedCountries.length < 2 && (
          <p className="text-xs text-zinc-500 mt-2">
            Select at least 2 countries to compare (max 4)
          </p>
        )}
      </div>

      {/* Comparison Results */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="loading-spinner" data-testid="loading-spinner"></div>
        </div>
      ) : comparisonData && selectedCountries.length >= 2 ? (
        <div className="space-y-4 animate-fade-in">
          {/* Header Cards - Compact */}
          <div className={`grid gap-2`} style={{gridTemplateColumns: `repeat(${selectedCountries.length}, 1fr)`}}>
            {comparisonData.countries?.map(country => (
              <div key={country.code} className="card p-3 text-center" data-testid={`country-header-${country.code}`}>
                <span className="text-3xl mb-1 block">{country.flag}</span>
                <h3 className="font-bold text-sm text-zinc-800">{country.name}</h3>
                <div className="flex items-center justify-center gap-1.5 mt-1.5">
                  <Badge variant="outline" className="text-xs">{country.code}</Badge>
                  {country.has_customizations && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Badge className="text-[10px] bg-amber-100 text-amber-700 flex items-center gap-0.5">
                            <Pencil className="w-2.5 h-2.5" />
                            Customized
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">This country has user-customized data</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                {country.has_customizations && (
                  <div className="mt-2">
                    <ViewHistoryButton
                      countryCode={country.code}
                      countryName={country.name}
                      countryFlag={country.flag}
                      hasCustomizations={country.has_customizations}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Packaging Comparison Table - Compact */}
          <div className="card overflow-hidden">
            <div className="p-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <h3 className="font-semibold text-zinc-800 text-sm flex items-center gap-1.5">
                <FileText className="w-4 h-4 text-violet-600" />
                Packaging EPR Comparison
              </h3>
              <div className="flex items-center gap-3 text-[10px] text-zinc-500">
                <span className="flex items-center gap-1">
                  <Pencil className="w-3 h-3 text-amber-500" />
                  User-customized
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  Yes
                </span>
                <span className="flex items-center gap-1">
                  <XCircle className="w-3 h-3 text-red-400" />
                  No
                </span>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[150px] text-xs">Attribute</TableHead>
                  {selectedCountries.map(code => (
                    <TableHead key={code} className="text-center text-xs">
                      {countries.find(c => c.country_code === code)?.flag_emoji} {code}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.packaging_comparison?.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium text-xs">{row.attribute}</TableCell>
                    {selectedCountries.map(code => {
                      const isCustomized = row.customized?.[code];
                      const value = row[code];
                      
                      return (
                        <TableCell key={code} className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {value === "Yes" ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                            ) : value === "No" ? (
                              <XCircle className="w-4 h-4 text-red-400" />
                            ) : (
                              <span className="text-xs">{value || "N/A"}</span>
                            )}
                            {isCustomized && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Pencil className="w-3 h-3 text-amber-500" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">User-customized value</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Detailed Comparison Sections - Compact */}
          <div className={`grid gap-3`} style={{gridTemplateColumns: `repeat(${selectedCountries.length}, 1fr)`}}>
            {selectedCountries.map(code => {
              const details = countryDetails[code];
              const packaging = details?.packaging || {};
              const hasCustomizations = details?.has_customizations;
              const customizedFields = comparisonData?.customized_fields?.[code] || [];
              
              const isFieldCustomized = (fieldPath) => customizedFields.includes(fieldPath);
              
              const CustomIndicator = ({ fieldPath }) => (
                isFieldCustomized(fieldPath) ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Pencil className="w-2.5 h-2.5 text-amber-500 inline ml-1" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">User-customized</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null
              );
              
              return (
                <div key={code} className="card p-3 space-y-3" data-testid={`detail-card-${code}`}>
                  <div className="flex items-center justify-between border-b pb-1.5">
                    <h4 className="font-semibold text-zinc-800 text-sm flex items-center gap-1.5">
                      <span>{details?.flag_emoji} {details?.country_name}</span>
                      {hasCustomizations && (
                        <Badge className="text-[9px] bg-amber-100 text-amber-700 flex items-center gap-0.5">
                          <Pencil className="w-2 h-2" />
                          Edited
                        </Badge>
                      )}
                    </h4>
                    {hasCustomizations && (
                      <ViewHistoryButton
                        countryCode={code}
                        countryName={details?.country_name}
                        countryFlag={details?.flag_emoji}
                        hasCustomizations={hasCustomizations}
                      />
                    )}
                  </div>
                  
                  {/* Regulation */}
                  <div>
                    <span className="text-[10px] text-zinc-500 block mb-0.5">
                      Regulation
                      <CustomIndicator fieldPath="packaging.regulation_name" />
                    </span>
                    <p className="text-xs font-medium">{packaging.regulation_name || "N/A"}</p>
                  </div>
                  
                  {/* Registration System */}
                  <div>
                    <span className="text-[10px] text-zinc-500 block mb-0.5">
                      Registration System
                      <CustomIndicator fieldPath="packaging.registration_system" />
                    </span>
                    <p className="text-xs font-medium">{packaging.registration_system || "N/A"}</p>
                    {packaging.registration_url && (
                      <a 
                        href={packaging.registration_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[10px] text-violet-600 hover:underline flex items-center gap-0.5 mt-0.5"
                      >
                        Register <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                  
                  {/* Reporting */}
                  <div>
                    <span className="text-[10px] text-zinc-500 block mb-0.5">
                      Reporting
                      <CustomIndicator fieldPath="packaging.reporting_deadline" />
                    </span>
                    <p className="text-xs">{packaging.reporting_frequency || "N/A"}</p>
                    {packaging.reporting_deadline && (
                      <Badge variant="outline" className="mt-0.5 text-[10px]">
                        <Calendar className="w-2.5 h-2.5 mr-0.5" />
                        {packaging.reporting_deadline}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Main PROs */}
                  <div>
                    <span className="text-[10px] text-zinc-500 block mb-0.5">
                      Main PROs
                      <CustomIndicator fieldPath="packaging.major_pros" />
                    </span>
                    <div className="space-y-0.5">
                      {packaging.major_pros?.slice(0, 3).map((pro, idx) => (
                        <a 
                          key={idx}
                          href={pro.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-zinc-700 hover:text-violet-600 flex items-center gap-0.5"
                        >
                          <Building2 className="w-2.5 h-2.5" />
                          {pro.name}
                        </a>
                      ))}
                    </div>
                  </div>
                  
                  {/* Fee Structure */}
                  {packaging.fee_structure && (
                    <div>
                      <span className="text-[10px] text-zinc-500 block mb-0.5">
                        Fee Structure
                        <CustomIndicator fieldPath="packaging.fee_structure" />
                      </span>
                      <p className="text-[10px]">{packaging.fee_structure.type || "N/A"}</p>
                      {packaging.fee_structure.base_rate && (
                        <Badge variant="outline" className="mt-0.5 text-[10px]">
                          {packaging.fee_structure.base_rate}
                        </Badge>
                      )}
                    </div>
                  )}
                  
                  {/* Penalties */}
                  {packaging.penalties && (
                    <div>
                      <span className="text-[10px] text-zinc-500 block mb-0.5">Key Penalty</span>
                      <p className="text-[10px] text-red-600">
                        {Object.values(packaging.penalties)[0]}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Quick Actions - Compact */}
          <div className="flex justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedCountries([])}>
              Clear
            </Button>
            <Button size="sm" asChild>
              <a href="/countries">
                <Globe className="w-3.5 h-3.5 mr-1.5" />
                View All Countries
              </a>
            </Button>
          </div>
        </div>
      ) : (
        <div className="card p-8 text-center">
          <GitCompare className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-zinc-700 mb-1">Start Comparing</h3>
          <p className="text-xs text-zinc-500 mb-4">
            Select 2-4 countries to see detailed EPR comparison
          </p>
          <div className="flex justify-center gap-1.5 flex-wrap">
            {["DE", "FR", "ES", "IT"].map(code => {
              const country = countries.find(c => c.country_code === code);
              return (
                <Button 
                  key={code} 
                  variant="outline" 
                  size="sm"
                  className="text-xs"
                  onClick={() => addCountry(code)}
                  data-testid={`quick-add-${code}`}
                >
                  {country?.flag_emoji} {country?.country_name}
                </Button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
